import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import { forwardRef } from 'preact/compat'

/**
 * Shared base prose styling for read-only markdown blocks. Callers add layout,
 * text-colour, or line-clamp utilities via `className`.
 */
const PROSE_CLASSES =
  'prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-headings:my-2'

export interface MarkdownViewProps {
  /** Markdown source to render read-only. */
  children: string
  /** Extra classes merged onto the prose wrapper (layout, colours, line-clamp, …). */
  className?: string
}

/**
 * Read-only renderer for user-entered markdown (Overview/details, Knowledge Base
 * entries, landing-page field previews).
 *
 * Renders with `remark-breaks` on top of `remark-gfm` so single line breaks survive.
 * The Tiptap editor (RichTextBox) shows those breaks in edit mode, but plain
 * CommonMark collapses a lone "\n" into a space — without `remark-breaks` the
 * read-only view loses every line break the user typed, which looks broken to
 * viewers who can't edit. ORB-414.
 */
export const MarkdownView = forwardRef<HTMLDivElement, MarkdownViewProps>(
  ({ children, className = '' }, ref) => (
    <div ref={ref} className={`${PROSE_CLASSES} ${className}`.trim()}>
      <Markdown remarkPlugins={[remarkGfm, remarkBreaks]}>{children}</Markdown>
    </div>
  ),
)

MarkdownView.displayName = 'MarkdownView'
